import React from "react";
import {
	Button,
	ClassNameProps,
	Column,
	Grid,
	Heading,
	makeClassName,
	Modal,
	ModalProps,
	Row,
	Rule,
	Section,
	StyleProps,
} from "@hex-insights/core";
import { EditMode } from "@hex-insights/forms";
import { Icon, IconContainer } from "../../../../Components/Icon";
import {
	Guardian,
	GuardianNameFragment,
	Person,
	PersonFilterInput,
	PersonNameAndImageFragment,
	Student,
	StudentNameFragment,
	User,
} from "../../../../Utilities";
import { RelationshipDeleteButton } from "../../../DeleteButtons";
import { RelationshipFormat } from "../../../Format";
import { RelationshipCustomForm } from "../../../Forms";
import { GuardianUtilities } from "../../../Utilities";
import * as AddressComponents from "../../Address";
import * as EmailAddressComponents from "../../EmailAddress";
import { SendMessageButton } from "../../MessageGroup";
import * as PhoneNumberComponents from "../../PhoneNumber";
import * as RelationshipComponents from "../../Relationship";
import { GuardianIconNameDescription } from "../General";
import styles from "./styles.module.css";

export type GuardianRelationshipModalProps = Pick<ModalProps, "ifRef" | "onClose"> & {
	relationship: RelationshipComponents.CardRelationship & {
		guardian: Pick<Guardian, "id"> & {
			person: Pick<Person, "id"> &
				PersonNameAndImageFragment & {
					phoneNumbers: PhoneNumberComponents.CardPhoneNumber[];
					emailAddresses: EmailAddressComponents.CardEmailAddress[];
					addresses: AddressComponents.CardAddress[];
					userID: User["id"] | null;
				};
		};
	};
	student: Pick<Student, "id"> & StudentNameFragment;
} & Partial<ClassNameProps & StyleProps>;

export function GuardianRelationshipModal({
	relationship: { guardian, ...relationship },
	student,
	ifRef,
	onClose,
	className,
	style,
}: GuardianRelationshipModalProps) {
	const { isModalOpen: isEditModalOpen, openModal: openEditModal, closeModal: closeEditModal } = Modal.useToggle();

	const initialPeopleFilterForAddress = React.useMemo<PersonFilterInput>(
		() => ({
			or: [
				{
					idEQ: guardian.person.id,
				},
				{
					guardian: [GuardianUtilities.getRelatedGuardiansFilterInput(guardian.id)],
				},
			],
		}),
		[guardian.id, guardian.person.id],
	);

	return (
		<React.Fragment>
			<Modal
				ifRef={ifRef}
				onClose={onClose}
				className={makeClassName(styles["guardian-relationship-modal"], className)}
				style={style}
			>
				<Modal.Header>
					<Row justify="space-between" align="center" style={{ paddingRight: "1.5rem" }}>
						<GuardianIconNameDescription
							guardian={guardian}
							description={RelationshipFormat.Fields.relationshipType(relationship.relationshipType)}
							size="large"
						/>

						{!!guardian.person.userID && (
							<SendMessageButton userID={guardian.person.userID} variant="primary">
								Message in Portal
							</SendMessageButton>
						)}
					</Row>
				</Modal.Header>
				<Modal.Body>
					<Column justify="spaced-start">
						<Section>
							<Section.Header>
								<Row justify="space-between" align="center">
									<Row justify="spaced-start" align="center">
										<IconContainer color="var(--theme-colors__blue--light)">
											<Icon.Users color="var(--theme-colors__blue)" size={18} />
										</IconContainer>

										<Heading.H3 noMargin>Relationship Details</Heading.H3>
									</Row>

									<Button variant="link" size="small" onClick={openEditModal} className="button--icon">
										<Icon.Gear size={16} className="block" />
									</Button>
								</Row>
							</Section.Header>
							<Section.Body>
								<RelationshipComponents.RelationshipCard
									relationship={relationship}
									className={styles["guardian-relationship-modal__relationship-card"]}
								/>
							</Section.Body>
						</Section>

						<Rule style={{ margin: "0 1rem 1rem" }} />

						<Grid tracks={2} gap="var(--general--spaced---horizontal-spacing)">
							<EmailAddressComponents.EmailAddressPersonListSection
								person={guardian.person}
								emailAddresses={guardian.person.emailAddresses}
							/>
							<PhoneNumberComponents.PhoneNumberPersonListSection
								person={guardian.person}
								phoneNumbers={guardian.person.phoneNumbers}
							/>
						</Grid>

						<AddressComponents.AddressListSection
							addresses={guardian.person.addresses}
							initialCreatePeopleFilter={initialPeopleFilterForAddress}
							columns={2}
						/>
					</Column>
				</Modal.Body>
			</Modal>

			<Modal.If condition={isEditModalOpen}>
				<EditModal relationship={relationship} student={student} guardian={guardian} onClose={closeEditModal} />
			</Modal.If>
		</React.Fragment>
	);
}

const editFormStateArgs = { editMode: EditMode.WriteOnly };

type EditModalProps = {
	relationship: Omit<RelationshipCustomForm.ControlledDetailProps["relationship"], "student" | "guardian">;
	student: Pick<Student, "id"> & StudentNameFragment;
	guardian: Pick<Guardian, "id"> & GuardianNameFragment;
} & Pick<ModalProps, "ifRef" | "onClose" | "className" | "style">;

function EditModal({ relationship, student, guardian, ifRef, onClose, className, style }: EditModalProps) {
	const onSuccess = React.useCallback(() => {
		onClose?.();
	}, [onClose]);

	const relationshipWithPeople = React.useMemo(
		() => ({
			...relationship,
			student,
			guardian,
		}),
		[relationship, student, guardian],
	);

	return (
		<Modal ifRef={ifRef} onClose={onClose} className={makeClassName(styles["edit-modal"], className)} style={style}>
			<Modal.Header>
				<Heading.H2 noMargin>Edit Relationship</Heading.H2>
			</Modal.Header>
			<Modal.Body>
				<Column justify="spaced-start">
					<RelationshipCustomForm.ControlledDetail
						relationship={relationshipWithPeople}
						formStateArgs={editFormStateArgs}
						noChangesDialog
						onSuccess={onSuccess}
					/>

					<RelationshipComponents.RelationshipDeletePermissionRequired>
						<RelationshipDeleteButton instanceID={relationship.id} size="small" onSuccess={onSuccess} />
					</RelationshipComponents.RelationshipDeletePermissionRequired>
				</Column>
			</Modal.Body>
		</Modal>
	);
}
